import DateSimple from '@/core/types/DateSimple';
import CurrencyRate from '@/core/types/CurrencyRate';

/**
 * Кеш курсов валют
 */
class CurrencyRatesCache {
    private static  cache = reactive(new Map<string, Record<string, CurrencyRate>>());

    /**
     * Пары «дата + валюта», по которым запрос курса уже отработал. Нужны
     * отображению, чтобы отличить «курс ещё едет» от «курса не существует»:
     * курсы хранятся не за все календарные дни и не за всю историю по всем
     * валютам, поэтому пустой результат — штатный и постоянный ответ, а не
     * задержка.
     *
     * Ключ по паре, а не по дате: на одну дату могут спросить курс одной
     * валюты и не спросить другой — вторая при отметке по дате получила бы
     * «курса нет» вместо «ещё не спрашивали».
     */
    private static requestedRates = reactive(new Set<string>());

    public static get(date: Date, currencyCode: string): CurrencyRate | null {
        const dateKey = this.getDateKey(date);
        const ratesForDate = this.cache.get(dateKey);

        return ratesForDate?.[currencyCode] ?? null;
    }

    public static getAll(date: Date): Record<string, CurrencyRate> | null {
        const dateKey = this.getDateKey(date);
        return this.cache.get(dateKey) ?? null;
    }

    public static set(date: Date, currencyCode: string, rate: CurrencyRate): void {
        const dateKey = this.getDateKey(date);

        if (!this.cache.has(dateKey)) {
            this.cache.set(dateKey, {});
        }

        const ratesForDate = this.cache.get(dateKey)!;
        ratesForDate[currencyCode] = rate;
    }

    public static setAll(date: Date, rates: Record<string, CurrencyRate>): void {
        const dateKey = this.getDateKey(date);
        this.cache.set(dateKey, { ...rates });
    }

    /**
     * Отмечает, что запрос курса по этой паре отработал. Вызывается на все
     * запрошенные пары, а не только на пришедшие: отметка нужна именно там,
     * где курса не оказалось.
     */
    public static markRequested(date: Date, currencyCode: string): void {
        this.requestedRates.add(`${this.getDateKey(date)}|${currencyCode}`);
    }

    /**
     * Отрабатывал ли уже запрос курса по этой паре.
     * true + отсутствие курса в кэше = курса не существует, ждать нечего.
     */
    public static isRequested(date: Date, currencyCode: string): boolean {
        return this.requestedRates.has(`${this.getDateKey(date)}|${currencyCode}`);
    }

    public static hasFull(date: Date, availableCurrencies: Set<string>): boolean {
        const rates = this.getAll(date);
        if (!rates) return false;

        const cachedCurrencies = new Set(Object.keys(rates));
        return availableCurrencies.symmetricDifference(cachedCurrencies).size === 0;
    }

    public static clear(date?: Date | string): void {
      const dateKey = this.getDateKey(date);
      this.cache.delete(dateKey);

      // отметки хранятся по парам «дата|валюта» — снимаем все за эту дату
      this.requestedRates.forEach(key => {
          if (key.startsWith(`${dateKey}|`)) {
              this.requestedRates.delete(key);
          }
      });
    }

    public static clearAll() {
      this.cache.clear();
      this.requestedRates.clear();
    }

    private static getDateKey(date: Date): string {
        const dateObj = DateSimple.isDateSimple(date) ? date : new DateSimple(date);
        return dateObj.toDateString();
    }
}

export default CurrencyRatesCache;